import React, { useEffect } from "react";
import { SVG } from "@components/items/common/SVG";
import { Separator } from "@ui/separator";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import {
  getCustomerQuota,
  getProductGroupContent,
  useAppSelector,
} from "@lib/stores";
import { Server } from "@wajeez/wajeez.microservices";

const CreditProducts = ({
  selectedProduct,
  onProductSelect,
  onProductSubmit,
}: {
  selectedProduct: Server.ProductGroupWithProductsOutput | null;
  onProductSelect: (product: Server.ProductGroupWithProductsOutput) => void;
  onProductSubmit: (product: Server.ProductGroupWithProductsOutput) => void;
}) => {
  const customerQuota = useAppSelector(
    (state) => state.payment.customerQuota.data
  );
  const products = useAppSelector(
    (state) => state?.payment?.productGroupContent?.data?.products
  );

  useEffect(() => {
    getProductGroupContent();
    getCustomerQuota();
  }, []);

  const handleProductClick = (
    product: Server.ProductGroupWithProductsOutput
  ) => {
    onProductSelect(product);
  };

  const handleSubmit = () => {
    if (selectedProduct) {
      onProductSubmit(selectedProduct);
    }
  };

  return (
    <div className="flex h-full flex-col gap-2 bg-white p-0 dark:bg-[#010D19]">
      <div className="flex flex-col items-center justify-center gap-4 text-center">
        <SVG
          src="/assets/icons/success-replacement.svg"
          className="max-md:w-[120px]"
        />
        <TranslatedText
          text="full-books.wajeez-book-balance"
          className="text-[32px] font-bold text-primary dark:text-[#64A6E1]"
        />
        <TranslatedText
          text="full-books.balance-description"
          className="text-[20px] font-normal leading-7 text-[#575757] dark:text-[#91949C]"
        />
        <div className="flex items-center justify-center gap-2 rounded-md border border-[#007FFF] bg-[#D4EAFF] px-5 py-3 text-[20px] font-medium">
          <TranslatedText text="ns1:full-books.book-balance" />
          <div>
            <SVG src="/assets/icons/coin.svg" className="h-6 w-6" />
          </div>
          <div>{customerQuota?.remainingCredit}</div>
        </div>
        <Separator className="mt-4 h-[0.5px] bg-[#8A8A8A]" />
        <div className="flex flex-col items-center justify-center">
          <TranslatedText
            text="full-books.balance-top-up"
            className="text-[24px] font-bold text-[#007684] dark:text-[#64A6E1]"
          />
          <TranslatedText
            text="full-books.top-up-description"
            className="text-[16px] font-normal text-[#5A5A5A] dark:text-[#91949C]"
          />
        </div>
        <div className="my-2 flex w-full flex-col md:gap-2 xl:gap-4">
          {products?.map((p) => {
            return (
              <Product
                key={p.id}
                price={p.name}
                amount={p.amount}
                currency={p.currency}
                isSelected={selectedProduct?.id === p.id}
                onSelect={() => handleProductClick(p)}
              />
            );
          })}
        </div>
      </div>
      <div className="flex w-full items-center justify-center">
        <ShadcnButton
          className="mx-auto !flex w-[80%] items-center justify-center py-6 text-[20px] text-white dark:bg-[#64A6E1] dark:text-[#010D19] dark:hover:bg-[#64A6E1]"
          disabled={!selectedProduct}
          onClick={handleSubmit}
        >
          <TranslatedText text="full-books.buy-now" />
        </ShadcnButton>
      </div>
    </div>
  );
};

export default CreditProducts;

const Product = ({
  price,
  amount,
  currency,
  isSelected,
  onSelect,
}: {
  price: string | undefined;
  amount: number | undefined;
  currency: string | undefined;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <div
      className={`flex w-full cursor-pointer items-center justify-between rounded-lg p-[9px] text-[20px] font-medium dark:text-white ${isSelected ? "border-2 border-primary dark:border-[#64A6E1]" : "border border-[#A0A0A0]"}`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-1.5">
        <SVG src="/assets/icons/coin.svg" className="h-6 w-6" />
        <div>{price}</div>
      </div>
      <div className="flex gap-2">
        <Separator
          orientation="vertical"
          className="h-8 bg-[#0C0C0C] dark:bg-[#91949C]"
        />
        <div className="me-auto">
          {currency}
          {amount}
        </div>
      </div>
    </div>
  );
};
